/**
 * M-A11 — Delete impact modal.
 *
 * Sprint 131 Stream C. Shows usage analysis (agents/hives/conversations using
 * the skill) and lets admin choose soft or hard delete. Hard delete requires
 * typing the slug to confirm.
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { AlertTriangle } from 'lucide-react';
import {
  useAdminSkillUsage,
  useDeleteAdminSkill,
  type SkillAssetCollection,
} from '@/features/skills';
import { useI18n } from '@/i18n';
import { getErrorMessage } from '@/lib/api-client';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  skillId: string;
  slug: string;
  /** System collections materializing the skill assets — deleted together
   * with the skill (ISS-289/ISS-302). */
  collections?: SkillAssetCollection[];
  onDeleted?: () => void;
}

export function SkillDeleteImpactModal({
  open,
  onOpenChange,
  skillId,
  slug,
  collections = [],
  onDeleted,
}: Props) {
  const [hard, setHard] = useState(false);
  const [confirmSlug, setConfirmSlug] = useState('');

  const usage = useAdminSkillUsage(open ? skillId : undefined);
  const del = useDeleteAdminSkill();

  const { t } = useI18n();

  useEffect(() => {
    if (!open) {
      setHard(false);
      setConfirmSlug('');
    }
  }, [open]);

  const hardConfirmed = hard && confirmSlug === slug;
  const inUse = Boolean(
    usage.data &&
      (usage.data.total_bindings > 0 || usage.data.conversations_active > 0),
  );

  async function handleDelete() {
    try {
      await del.mutateAsync({
        skillId,
        hard,
        confirm: hard ? { confirm_slug: slug } : undefined,
      });
      toast.success(
        hard
          ? t('admin.skills.deleteImpact.deleted', { slug })
          : t('admin.skills.deleteImpact.archived', { slug }),
      );
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {t('admin.skills.deleteImpact.title')}
          </DialogTitle>
          <DialogDescription>
            {t('admin.skills.deleteImpact.reviewImpact')} <code>{slug}</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {usage.isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Spinner />
            </div>
          ) : usage.data ? (
            <div className="rounded-md border p-3 text-sm space-y-1 bg-muted/40">
              <p>
                {t('admin.skills.deleteImpact.agentBindings', { count: usage.data.total_bindings })}
              </p>
              <p>
                {t('admin.skills.deleteImpact.agentsRef', { count: usage.data.agents.length })}
              </p>
              <p>
                {t('admin.skills.deleteImpact.hivesRef', { count: usage.data.hives.length })}
              </p>
              <p>
                {t('admin.skills.deleteImpact.activeConversations', { count: usage.data.conversations_active })}
              </p>
            </div>
          ) : null}

          {inUse && (
            <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200">
              {t('admin.skills.deleteImpact.inUseWarning', {
                agents: usage.data?.agents.length ?? 0,
                hives: usage.data?.hives.length ?? 0,
                conversations: usage.data?.conversations_active ?? 0,
              })}
            </div>
          )}

          {collections.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm space-y-1">
              <p className="font-medium">
                {t('admin.skills.deleteImpact.resourceCollections')}
              </p>
              <ul className="list-disc list-inside text-amber-800 dark:text-amber-200">
                {collections.map((c) => (
                  <li key={c.collection_id}>
                    <code>{c.name}</code>
                    {typeof c.document_count === 'number'
                      ? ` — ${t('admin.skills.deleteImpact.resourceDocs', { count: c.document_count })}`
                      : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={hard}
              onChange={(e) => setHard(e.target.checked)}
              className="mt-1"
            />
            <span>
              {t('admin.skills.deleteImpact.hardDelete')}
            </span>
          </label>

          {hard && (
            <div className="space-y-1">
              <Label>
                {t('admin.skills.deleteImpact.confirmSlug', { slug })}
              </Label>
              <Input
                value={confirmSlug}
                onChange={(e) => setConfirmSlug(e.target.value)}
                placeholder={slug}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={del.isPending || (hard && !hardConfirmed) || inUse}
          >
            {del.isPending
              ? t('admin.skills.deleteImpact.deleting')
              : hard
                ? t('admin.skills.deleteImpact.permanentlyDelete')
                : t('admin.skills.deleteImpact.archive')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
